import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAlert,
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
  MiMenu,
  MiModalConfirm,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IMaterialsResponse } from "@miplatform/types";
import {
  filterMaterialsByAttributes,
  filterMaterialsByCategory,
  filterMaterialsBySearch,
} from "@miplatform/utils";
import {
  mdiHexagonOutline,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { CategoryFilterDrawer } from "../../components/FilterDrawer/CategoryFilterDrawer";
import { MaterialCard } from "../../components/MaterialCard/MaterialCard";
import { useItemsViewModel } from "./useItemsViewModel";

export function Items() {
  const [selectedMaterial, setSelectedMaterial] =
    useState<IMaterialsResponse | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { navigate } = useRouter();
  const { classes, theme } = useStyles();
  const { hasPermission } = usePermissions();

  const canCreateItem = hasPermission(PERMISSIONS.MATERIAL.CREATE);
  const canDeleteItem = hasPermission(PERMISSIONS.MATERIAL.DELETE);
  const canUpdateItem = hasPermission(PERMISSIONS.MATERIAL.UPDATE);

  const {
    materials,
    filteredMaterials,
    setMaterials,
    fetchMaterials,
    refresh,
    deleteMaterial,
    fetchCategoryFilters,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    search,
    debouncedSearch,
    setSearch,
    handleReset,
    handleCategoryClick,
    handleAttributeValueClick,
    isLoading,
    setFilteredMaterials,
    hasFilters,
  } = useItemsViewModel();

  useEffect(() => {
    fetchCategoryFilters();
  }, []);

  useEffect(() => {
    let filtered = [...materials];
    if (selectedCategory?.categoryId) {
      filtered = filterMaterialsByCategory(filtered, selectedCategory);
    }
    if (Object.keys(selectedAttributeValues).length > 0) {
      filtered = filterMaterialsByAttributes(filtered, selectedAttributeValues);
    }
    if (debouncedSearch) {
      filtered = filterMaterialsBySearch(filtered, debouncedSearch);
    }
    setFilteredMaterials(filtered);
  }, [materials, selectedCategory, selectedAttributeValues, debouncedSearch]);

  const sections = [
    {
      fieldset: [
        {
          renderCell: (item: IMaterialsResponse) => (
            <MaterialCard
              item={item}
              rightAction={renderItemActions(item)}
              noCardStyle
              showPrice
              renderCustomDetails={
                <MiStackedTypography title="Steps" description="--" />
              }
            />
          ),
        },
      ],
    },
  ];

  const handleNavigate = (id: string) => {
    navigate(`/estimating/configuration/items/${id}`);
  };

  const renderItemActions = (item: IMaterialsResponse) => {
    const actions = [];
    if (canUpdateItem) {
      actions.push({
        label: BUTTON_LABELS.EDIT,
        icon: mdiPencilOutline,
        action: () => handleNavigate(item.id),
      });
    }
    if (canDeleteItem) {
      actions.push({
        label: BUTTON_LABELS.DELETE,
        icon: mdiTrashCanOutline,
        action: () => handleOpenDeleteModal(item),
        color: theme.palette.red.main,
      });
    }

    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={actions} />
      </Box>
    );
  };

  const handleOpenDeleteModal = (item: IMaterialsResponse) => {
    setSelectedMaterial(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedMaterial) {
      deleteMaterial(selectedMaterial);
      setOpenDeleteModal(false);
    }
  };

  return (
    <>
      <MiBuildPageHeader
        title="Items"
        loading={isLoading}
        titleIcon={
          <MiIcon path={mdiHexagonOutline} color={theme.palette.primary.main} />
        }
        actionComponent={
          canCreateItem && (
            <MiButton
              title="Item"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{ path: mdiPlus }}
              onClick={() => navigate("/estimating/configuration/add-items")}
              data-testid="add-item-btn"
            />
          )
        }
        actionContainerStyle={{
          flexWrap: "nowrap",
        }}
      />
      <Box className={classes.root}>
        <MiBox
          testid="items-component"
          overflow="auto"
          height="auto"
          className={classes.viewContainer}
        >
          <MiAlert
            title="Select a Category on the right to begin filtering"
            description="You can also search for specific items using the search bar"
            storageKey="viewMaterials-alert"
          />
          <MiList
            viewType="card"
            cardProps={{
              sections,
              onClick: (item: IMaterialsResponse) => handleNavigate(item.id),
              leftCardProps: { fieldset: [] },
            }}
            loading={isLoading}
            data={filteredMaterials}
            setData={setMaterials}
            dataProvider={fetchMaterials}
            refresh={refresh}
            paginationMode={PAGINATION.CLIENT}
            emptyListProps={{
              text: "No Items found",
              actions: hasFilters
                ? [
                    {
                      title: "Reset Filters",
                      onClick: handleReset,
                      variant: BUTTON_VARIANTS.OUTLINED,
                    },
                  ]
                : [],
            }}
            data-testid="items-list"
          />
        </MiBox>
        <CategoryFilterDrawer
          title="Filter Items"
          categoryFilterOptions={categoryFilterOptions}
          search={search}
          selectedCategory={selectedCategory}
          selectedAttributeValues={selectedAttributeValues}
          setSearch={setSearch}
          handleCategoryClick={handleCategoryClick}
          handleAttributeValueClick={handleAttributeValueClick}
          handleReset={handleReset}
          handleResetSelection={handleReset}
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          open={openDeleteModal}
          textPositive={BUTTON_LABELS.DELETE}
          buttonPositiveColor={BUTTON_COLORS.RED}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.red.main,
          }}
          handleNegative={() => setOpenDeleteModal(false)}
          handlePositive={handleConfirmDeleteModal}
          description={`You are about to delete ${selectedMaterial?.category?.itemCode} - ${selectedMaterial?.category?.itemDescription}. Are you sure you would like to continue?`}
        />
      </Box>
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "auto",
    height: "100%",
  },
  viewContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    gap: theme.spacing(2),
  },
}));
